import type { Position } from '../types';

export const geolocationService = {
  async getCurrentPosition(): Promise<Position> {
    return new Promise<Position>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      const onSuccess = (position: GeolocationPosition) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      };

      const onFinalError = (error: GeolocationPositionError) => {
        let message = 'Unable to retrieve your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message =
              'GPS permission is required to check in. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Location information is unavailable. Please ensure GPS is enabled.';
            break;
          case error.TIMEOUT:
            message = 'Location request timed out. Please move to an open area and try again.';
            break;
        }
        reject(new Error(message));
      };

      // Stage 1: try high-accuracy GPS with a short timeout
      navigator.geolocation.getCurrentPosition(onSuccess, (error) => {
        if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
          // Stage 2: fall back to low-accuracy (network/IP-based) with a longer timeout
          navigator.geolocation.getCurrentPosition(onSuccess, onFinalError, {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 60000,
          });
        } else {
          onFinalError(error);
        }
      }, { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 });
    });
  },

  isAvailable(): boolean {
    return 'geolocation' in navigator;
  },

  async requestPermission(): Promise<boolean> {
    if (!this.isAvailable()) {
      throw new Error('Geolocation is not supported by your browser');
    }
    try {
      await this.getCurrentPosition();
      return true;
    } catch {
      return false;
    }
  },
};
